import type * as DB from 'interfaces/database';

/**
 * Checks whether a record owns a property without reading through its prototype chain.
 *
 * @param value Record to inspect.
 * @param property Property name to check.
 * @returns Whether the record has the property as an own property.
 */
function hasOwnProperty(
	value: Record<string, unknown>,
	property: string,
): boolean {
	return Object.prototype.hasOwnProperty.call(
		value,
		property,
	);
}

/**
 * Narrows arbitrary data to a plain object-like record that can be inspected for
 * template text attribute keys.
 *
 * @param value Value to inspect.
 * @returns Whether the value is a non-null, non-array object.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
	return (
		typeof value === 'object'
		&& value !== null
		&& !Array.isArray(value)
	);
}

/**
 * Converts legacy flat text-position attributes and structured text-position
 * attributes into the single shape consumed by editor rendering code.
 *
 * @param attributes Parsed {@link DB.TemplatePositionTextAttributes} data, or no attributes.
 * @returns Normalized placeholder and style data for a template text position.
 */
export function normalizeTemplatePositionTextAttributes(
	attributes: DB.TemplatePositionTextAttributes | null | undefined,
): DB.TemplatePositionTextAttributesModel {
	const normalizedAttributes: DB.TemplatePositionTextAttributesModel = {};

	if (!isRecord(attributes)) {
		return normalizedAttributes;
	}

	const hasPlaceholder = hasOwnProperty(
		attributes,
		'placeholder',
	);
	const hasStyles = hasOwnProperty(
		attributes,
		'styles',
	);

	/**
	 * Legacy attributes are a flat CSS style map. The structured format is
	 * identified by the presence of either supported top-level marker key.
	 */
	if (
		!hasPlaceholder
		&& !hasStyles
	) {
		normalizedAttributes.styles = attributes as DB.TemplatePositionTextAttributesLegacyModel;
		return normalizedAttributes;
	}

	if (typeof attributes.placeholder === 'string') {
		const placeholder = attributes.placeholder.trim();

		if (placeholder.length > 0) {
			normalizedAttributes.placeholder = placeholder;
		}
	}

	if (isRecord(attributes.styles)) {
		normalizedAttributes.styles = attributes.styles as Record<string, string>;
	}

	return normalizedAttributes;
}

/**
 * Checks whether normalized text-position attributes contain data that should
 * be persisted on the template text position model.
 *
 * @param attributes Normalized template text position attributes.
 * @returns Whether placeholder or style data is available.
 */
export function hasTemplatePositionTextAttributes(attributes: DB.TemplatePositionTextAttributesModel): boolean {
	return (
		typeof attributes.placeholder !== 'undefined'
		|| typeof attributes.styles !== 'undefined'
	);
}
